import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  deleteBill,
  listBills,
  markBillPaid,
} from "../lib/api-modules/bills.js";
import {
  BILL_RECURRENCE_LABELS,
  PAYMENT_METHOD_LABELS,
  type Bill,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { bucketBills, type BillsBuckets } from "../lib/util/bills-bucket.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { NotesPanel } from "../components/notes-panel.js";
import { DecisionsPanel } from "../components/decisions-panel.js";
import { BillForm } from "../components/bills/BillForm.js";
import { CostPerUseTile } from "../components/bills/CostPerUseTile.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";
import {
  Button,
  Card,
  EmptyState,
  Pill,
  SectionHeader,
} from "../components/design-system/index.js";

/**
 * /bills — household-scoped CRUD for bills + subscriptions.
 *
 * Phase 5 Session 2 introduced the page; Phase 5 Session 6 restyles it
 * to design-system primitives + embeds <CostPerUseTile> below
 * subscription bills + surfaces price-change history (when the
 * subscription audit has detected drift).
 *
 * Bills bucket into Overdue → Due 7 days → Due 30 days → Later, with
 * archived bills hidden behind a toggle. The Mark-paid action is
 * server-side: the server reads recurrence + start_date, computes the
 * next due_date (re-anchoring across short months), and archives
 * one-time bills.
 */

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

const BUCKET_TONE: Record<"red" | "amber" | "neutral", "danger" | "warning" | "neutral"> = {
  red: "danger",
  amber: "warning",
  neutral: "neutral",
};

export function BillsPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-5xl p-6 lg:p-8">
      <SectionHeader
        title="Bills"
        sub="Recurring bills + one-time bills + subscriptions. Mark paid advances the due date by the recurrence interval."
      />
      {!canWrite && (
        <Card className="mb-6 border-dashed">
          <p className="text-sm text-[var(--color-muted)]">
            Read-only access — ask an owner or admin to make changes.
          </p>
        </Card>
      )}
      <BillsList canWrite={canWrite} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.bills} />
      </div>
    </div>
  );
}

function BillsList({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data, error, isLoading } = useQuery({
    queryKey: ["bills", { includeArchived: showArchived }],
    queryFn: () => listBills(showArchived),
  });

  const markPaidMutation = useMutation({
    mutationFn: (billId: string) => markBillPaid(billId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bills"] });
      toast.show("Marked paid");
    },
    onError: (err) => {
      toast.show(
        err instanceof ApiError
          ? `Couldn't mark paid: ${err.message}`
          : "Couldn't mark bill as paid",
      );
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (billId: string) => deleteBill(billId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["bills"] });
      toast.show("Bill archived");
    },
    onError: () => toast.show("Couldn't archive bill"),
  });

  const today = new Date().toISOString().slice(0, 10);
  const buckets: BillsBuckets = useMemo(() => {
    const bills = data?.bills ?? [];
    return bucketBills(bills, today);
  }, [data, today]);

  if (isLoading) {
    return (
      <p className="text-sm text-[var(--color-muted)]" role="status">
        Loading bills…
      </p>
    );
  }
  if (error) {
    return (
      <ErrorMessage>
        {error instanceof ApiError ? error.message : "Couldn't load bills."}
      </ErrorMessage>
    );
  }

  const total = data?.bills?.length ?? 0;
  const isMutating = markPaidMutation.isPending || archiveMutation.isPending;

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[var(--color-muted)]">
          {total} {total === 1 ? "bill" : "bills"}
          {showArchived ? " (incl. archived)" : ""}
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-[var(--color-muted)]">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
          {canWrite && !showCreate && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setShowCreate(true)}
            >
              Add bill
            </Button>
          )}
        </div>
      </div>

      {showCreate && (
        <div className="mb-4">
          <BillForm mode="create" onClose={() => setShowCreate(false)} />
        </div>
      )}

      {total === 0 && !showCreate && (
        <EmptyState
          title="No bills yet"
          description={canWrite ? 'Click "Add bill" to start.' : ""}
        />
      )}

      <BucketSection
        title="Overdue"
        accent="red"
        bills={buckets.overdue}
        canWrite={canWrite}
        editingId={editingId}
        onEdit={setEditingId}
        onMarkPaid={(id) => markPaidMutation.mutate(id)}
        onArchive={(id) => archiveMutation.mutate(id)}
        isMutating={isMutating}
      />
      <BucketSection
        title="Due in 7 days"
        accent="amber"
        bills={buckets.due7d}
        canWrite={canWrite}
        editingId={editingId}
        onEdit={setEditingId}
        onMarkPaid={(id) => markPaidMutation.mutate(id)}
        onArchive={(id) => archiveMutation.mutate(id)}
        isMutating={isMutating}
      />
      <BucketSection
        title="Due in 30 days"
        accent="neutral"
        bills={buckets.due30d}
        canWrite={canWrite}
        editingId={editingId}
        onEdit={setEditingId}
        onMarkPaid={(id) => markPaidMutation.mutate(id)}
        onArchive={(id) => archiveMutation.mutate(id)}
        isMutating={isMutating}
      />
      <BucketSection
        title="Later"
        accent="neutral"
        bills={buckets.later}
        canWrite={canWrite}
        editingId={editingId}
        onEdit={setEditingId}
        onMarkPaid={(id) => markPaidMutation.mutate(id)}
        onArchive={(id) => archiveMutation.mutate(id)}
        isMutating={isMutating}
      />
      {showArchived && buckets.archived.length > 0 && (
        <BucketSection
          title="Archived"
          accent="neutral"
          bills={buckets.archived}
          canWrite={canWrite}
          editingId={editingId}
          onEdit={setEditingId}
          onMarkPaid={(id) => markPaidMutation.mutate(id)}
          onArchive={(id) => archiveMutation.mutate(id)}
          isMutating={isMutating}
        />
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function BucketSection({
  title,
  accent,
  bills,
  canWrite,
  editingId,
  onEdit,
  onMarkPaid,
  onArchive,
  isMutating,
}: {
  title: string;
  accent: "red" | "amber" | "neutral";
  bills: Bill[];
  canWrite: boolean;
  editingId: string | null;
  onEdit: (id: string | null) => void;
  onMarkPaid: (id: string) => void;
  onArchive: (id: string) => void;
  isMutating: boolean;
}) {
  if (bills.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[var(--color-muted)]">
        <Pill tone={BUCKET_TONE[accent]}>{title}</Pill>
        <span className="text-xs font-normal text-[var(--color-faint)]">
          {bills.length}
        </span>
      </h2>
      <ul className="flex flex-col gap-3">
        {bills.map((b) => (
          <li key={b.bill_id}>
            {editingId === b.bill_id ? (
              <BillForm mode="edit" bill={b} onClose={() => onEdit(null)} />
            ) : (
              <BillCard
                bill={b}
                canWrite={canWrite}
                onEdit={() => onEdit(b.bill_id)}
                onMarkPaid={() => onMarkPaid(b.bill_id)}
                onArchive={() => onArchive(b.bill_id)}
                isMutating={isMutating}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BillCard({
  bill,
  canWrite,
  onEdit,
  onMarkPaid,
  onArchive,
  isMutating,
}: {
  bill: Bill;
  canWrite: boolean;
  onEdit: () => void;
  onMarkPaid: () => void;
  onArchive: () => void;
  isMutating: boolean;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const isSubscription =
    bill.recurrence !== "one_time" && !bill.is_archived;

  // Most recent prior price (audit-tracked) — surface alongside the
  // current amount so users see "was $X, now $Y" inline.
  const history = bill.amount_cents_history ?? [];
  const priorObservation =
    history.length >= 2 ? history[history.length - 2] : null;
  const priorAmountCents = priorObservation?.amount_cents;
  const hasPriceChange =
    priorAmountCents !== undefined && priorAmountCents !== bill.amount_cents;

  return (
    <Card padded>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 truncate text-base font-medium text-[var(--color-text)]">
            {bill.name}
            {bill.is_archived && (
              <Pill tone="neutral">archived</Pill>
            )}
            {bill.autopay && <Pill tone="accent">autopay</Pill>}
            {hasPriceChange && (
              <Pill tone="warning">
                price changed
              </Pill>
            )}
          </h3>
          <p className="mt-1 text-xs text-[var(--color-muted)]">
            <span className="font-mono tabular-nums text-[var(--color-text)]">
              {formatCentsCurrency(bill.amount_cents)}
            </span>
            {hasPriceChange && priorAmountCents !== undefined && (
              <span className="ml-1 text-[var(--color-faint)]">
                (was {formatCentsCurrency(priorAmountCents)})
              </span>
            )}{" "}
            · {BILL_RECURRENCE_LABELS[bill.recurrence]} · due{" "}
            <time dateTime={bill.due_date}>{bill.due_date}</time>
            {bill.payee && <> · {bill.payee}</>}
            {bill.payment_method && (
              <> · {PAYMENT_METHOD_LABELS[bill.payment_method]}</>
            )}
            {bill.category && <> · {bill.category}</>}
          </p>
          {bill.last_paid_date && (
            <p className="mt-0.5 text-xs text-[var(--color-muted)]">
              Last paid <time dateTime={bill.last_paid_date}>{bill.last_paid_date}</time>
            </p>
          )}
          {bill.description && (
            <p className="mt-2 text-sm text-[var(--color-text)]">
              {bill.description}
            </p>
          )}
        </div>
        {canWrite && (
          <div className="flex shrink-0 items-center gap-1.5">
            {!bill.is_archived && (
              <ConfirmButton
                label="Mark paid"
                pendingLabel="Marking…"
                size="xs"
                ariaLabel={`Mark ${bill.name} paid`}
                onConfirm={onMarkPaid}
                isPending={isMutating}
              />
            )}
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={onEdit}
              disabled={isMutating}
            >
              Edit
            </Button>
            {!bill.is_archived && (
              <ConfirmButton
                label="Archive"
                pendingLabel="Archiving…"
                size="xs"
                ariaLabel={`Archive ${bill.name}`}
                onConfirm={onArchive}
                isPending={isMutating}
              />
            )}
          </div>
        )}
      </header>

      {isSubscription && <CostPerUseTile bill={bill} />}

      <div className="mt-3 border-t border-[var(--color-border-subtle)] pt-3">
        <button
          type="button"
          onClick={() => setShowDetail((s) => !s)}
          className="text-xs text-[var(--color-muted)] hover:text-[var(--color-text)] hover:underline"
        >
          {showDetail ? "Hide notes & decisions" : "Show notes & decisions"}
        </button>
        {showDetail && (
          <div className="mt-3 flex flex-col gap-4">
            <NotesPanel entityType="bill" entityId={bill.bill_id} />
            <DecisionsPanel entityType="bill" entityId={bill.bill_id} />
          </div>
        )}
      </div>
    </Card>
  );
}
